import React, { useContext, useEffect, useState } from "react";
import styled from "styled-components";
import {
    Button,
    FlexContainer,
    LottieLoadingIndicator,
    StandardModalActionGroup,
    StandardModalDescription,
    StandardModalDivider,
    StandardModal,
    StandardModalSection,
    StandardModalSectionTitle,
    Toast
} from "src/components";
import { TextLabel } from "../style";
import { LoadingIndicatorButton } from "src/scenes/Survey/components/Utils";
import { ToggleSwitch } from "../table/ToggleSwitch";
import { BodyAndMindContext } from "../../BodyAndMindContext";
import { getCompanyBodyAndMindSettings, updateRecentFromMeApp } from "../../api";

const NewsFeedModalContent = styled.div`
    width: 100%;
    min-width: 0;
    max-width: 100%;
    display: grid;
    gap: 1rem;
    padding: 1.2rem 1.6rem 0;
    box-sizing: border-box;
`;

const NoteRow = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 0.35rem;
    min-width: 0;
    flex-wrap: wrap;
`;

const ToggleCard = styled.div`
    width: 100%;
    min-width: 0;
    max-width: 26rem;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    border: 1px solid #D8DEEC;
    border-radius: 10px;
    padding: 0.95rem;
    box-sizing: border-box;

    @media (max-width: 520px) {
        align-items: flex-start;
        flex-direction: column;
    }
`;

export default function ShowNewsFeed() {
    const {
        recentFromMeApp,
        setRecentFromMeApp,
        toggleShowNewsFeedOption,
        showNewsFeedOption
    } = useContext(BodyAndMindContext);
    const [feedSettingIsFetching, setFeedSettingIsFetching] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [showFeeds, setShowFeeds] = useState(false);

    const onChangeShowRecent = () => {
        setShowFeeds(!showFeeds);
    };

    const getRecentFromMeAppStatus = async () => {
        setFeedSettingIsFetching(true);
        try {
            const response = await getCompanyBodyAndMindSettings();
            setShowFeeds(response.recentFromMeApp);
            setRecentFromMeApp(response.recentFromMeApp);
        } catch (error) {
            console.log("Error: ", error);
            Toast.error(error.getApiErrorDetails().message);
        } finally {
            setFeedSettingIsFetching(false);
        }
    };

    useEffect(() => {
        if (recentFromMeApp === null) {
            getRecentFromMeAppStatus();
        } else {
            setShowFeeds(recentFromMeApp);
        }
    }, [showNewsFeedOption, recentFromMeApp]);

    const onSubmit = async () => {
        setIsLoading(true);
        try {
            const response = await updateRecentFromMeApp({
                recentFromMeApp: showFeeds,
            });
            if (response) {
                Toast.success("Content settings updated successfully.");
            }
            setRecentFromMeApp(showFeeds);
            setIsLoading(false);
            toggleShowNewsFeedOption();
        } catch (error) {
            console.log("Error", error);
            Toast.error(error.getApiErrorDetails().message);
            setIsLoading(false);
        }
    };

    if (feedSettingIsFetching) {
        return (
            <FlexContainer width="100%" height="20rem">
                <LottieLoadingIndicator />
            </FlexContainer>
        );
    }

    return (
        <StandardModal
            open={true}
            onClose={toggleShowNewsFeedOption}
            title="Show News Feed From Me Business"
            description="Choose whether to display the Latest from Me Business news feed in your Body & Mind section."
            widthMode="medium"
            footer={(
                <StandardModalActionGroup $pushRight>
                    <Button
                        disabled={isLoading}
                        type="button"
                        onClick={() => toggleShowNewsFeedOption()}
                        border="1px solid #000"
                        color="#000"
                        width="140px"
                        height="38px"
                        style={{ borderRadius: "7px", boxShadow: "none" }}
                    >
                        Cancel
                    </Button>
                    {isLoading ? (
                        <LoadingIndicatorButton>
                            <LottieLoadingIndicator
                                height="2rem"
                                width="2rem"
                                margin="5px"
                                containerHeight="2rem"
                            />
                        </LoadingIndicatorButton>
                    ) : (
                        <Button
                            type="button"
                            backgroundColor="#171A22"
                            border="1px solid #171A22"
                            color="#fff"
                            width="140px"
                            height="38px"
                            style={{ borderRadius: "7px", boxShadow: "none" }}
                            onClick={() => onSubmit()}
                        >
                            Save
                        </Button>
                    )}
                </StandardModalActionGroup>
            )}
        >
            <NewsFeedModalContent>
                <StandardModalSection>
                    <StandardModalDescription>
                    Choose whether to display the Latest from Me Business news feed in your Body &
                    Mind section. This feed includes platform updates, feature announcements, and
                    tips from Me Business.
                    </StandardModalDescription>
                    <NoteRow>
                        <TextLabel fontSize="14px" fontWeight="bold">
                        Note:
                        </TextLabel>
                        <StandardModalDescription>
                        Some updates may relate to features not included in your current
                        subscription.
                        </StandardModalDescription>
                    </NoteRow>
                </StandardModalSection>

                <StandardModalDivider />

                <StandardModalSection>
                    <StandardModalSectionTitle>
                    Show News Feed From Me Business (Optional)
                    </StandardModalSectionTitle>
                    <StandardModalDescription color="#612684">
                    Changes apply instantly to all users in your business account. You can update
                    this setting at any time.
                    </StandardModalDescription>
                    <ToggleCard>
                        <TextLabel marginBottom="0px" fontSize="15px" fontWeight="bold">
                        Latest from Me Business
                        </TextLabel>
                        <FlexContainer
                            direction="row"
                            tDirection="row"
                            mDirection="row"
                            justifyContent="center"
                            alignItems="center"
                            gap="10px"
                        >
                            <TextLabel
                                marginBottom="0px"
                                fontSize="15px"
                                fontWeight="bold"
                                color="#D8DEEC"
                            >
                            Hide/Show
                            </TextLabel>
                            <ToggleSwitch onChange={() => onChangeShowRecent()} checked={showFeeds} />
                        </FlexContainer>
                    </ToggleCard>
                </StandardModalSection>
            </NewsFeedModalContent>
        </StandardModal>
    );
}
